import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BadgeEuro, CheckCircle2, Loader2, ArrowRight, AlertTriangle } from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

export function RevenueFixModal({ open, onClose, scanning, result, applying, onApply }) {
  const candidates = result?.candidates || [];
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) return;
    // Pre-select the strong (likely VAT) candidates
    const next = {};
    candidates.forEach((c) => { next[c.order_id] = !!c.likely_vat; });
    setSelected(next);
  }, [open, result]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedIds = useMemo(
    () => candidates.filter((c) => selected[c.order_id]).map((c) => c.order_id),
    [candidates, selected]
  );

  const allChecked = candidates.length > 0 && candidates.every((c) => selected[c.order_id]);
  const toggleAll = () => {
    const next = {};
    candidates.forEach((c) => { next[c.order_id] = !allChecked; });
    setSelected(next);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="revenue-fix-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeEuro className="w-5 h-5 text-orange-500" />
            Correction des revenus (Total TTC)
            <span className="text-sm font-normal text-zinc-500">montant de TVA détecté à la place du Total TTC</span>
          </DialogTitle>
        </DialogHeader>

        {scanning ? (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-500 gap-3" data-testid="revenue-scan-loading">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            <p className="text-sm">Analyse des commandes en cours…</p>
          </div>
        ) : candidates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-zinc-500 gap-2" data-testid="no-revenue-errors">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="text-sm font-medium text-zinc-700">Aucun revenu erroné détecté</p>
            <p className="text-xs">
              {result ? `${result.scanned} commande(s) vérifiée(s)` : ""}
              {result?.no_source ? ` · ${result.no_source} sans texte source (non vérifiable)` : ""}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-zinc-500 px-1">
              <span>
                <strong className="text-zinc-700">{candidates.length}</strong> commande(s) à corriger
                {result?.no_source ? ` · ${result.no_source} sans source (ignorées)` : ""}
              </span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-orange-500" data-testid="revenue-select-all" />
                Tout sélectionner
              </label>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1 mt-2">
              {candidates.map((c) => (
                <label
                  key={c.order_id}
                  className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer text-sm transition-colors ${
                    selected[c.order_id] ? "border-orange-400 bg-orange-50" : "border-zinc-200 bg-white hover:bg-zinc-50"
                  }`}
                  data-testid={`revenue-candidate-${c.order_id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[c.order_id]}
                    onChange={() => setSelected((prev) => ({ ...prev, [c.order_id]: !prev[c.order_id] }))}
                    className="accent-orange-500 flex-shrink-0"
                    data-testid={`revenue-check-${c.order_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800 truncate">{c.name || "—"}</span>
                      {c.date ? <span className="text-zinc-400 text-xs">{c.date}</span> : null}
                      {c.likely_vat ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full">
                          <AlertTriangle className="w-3 h-3" /> TVA probable
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-red-500 line-through">{fmtEur(c.current_revenue)}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-zinc-400" />
                      <span className="text-green-600 font-semibold">{fmtEur(c.corrected_revenue)}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="flex justify-between items-center pt-3 border-t border-zinc-100">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="close-revenue-fix-modal">Fermer</Button>
          {candidates.length > 0 && (
            <Button
              size="sm"
              onClick={() => onApply(selectedIds)}
              disabled={applying || selectedIds.length === 0}
              className="gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="apply-revenue-fix-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Corriger la sélection ({selectedIds.length})
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
